import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react";
import { api, tokenStorage } from "@/lib/api";
import type { User } from "@/lib/auth";
import {
	storeUser,
	getStoredUser,
	clearStoredUser,
	isAuthenticated,
} from "@/lib/auth";

// =====================
// Types
// =====================

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => void;
	updateUser: (user: User) => void;
}

interface LoginResponse {
	accessToken: string;
	refreshToken: string;
	user: User;
}

// =====================
// Context
// =====================

const AuthContext = createContext<AuthContextType | null>(null);

// =====================
// Provider
// =====================

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Check for existing auth on mount
	useEffect(() => {
		const checkAuth = async () => {
			const storedUser = getStoredUser();
			const hasToken = isAuthenticated();

			if (storedUser && hasToken) {
				// Verify token is still valid
				try {
					const response = await api.get<{ user: User }>("/auth/me");

					if (response.data?.user) {
						// Token is valid - use server data
						setUser(response.data.user);
						storeUser(response.data.user);
					} else if (response.status === 401) {
						// Token invalid/expired - clear auth immediately
						// (do NOT fall back to stored user)
						clearAuth();
					}
					// For other error statuses (500, etc.), fall through to catch
				} catch {
					// Network error or server unreachable - use stored user as fallback
					// This allows offline browsing with cached data
					setUser(storedUser);
				}
			}

			setIsLoading(false);
		};

		checkAuth();

		// Listen for token clearing events (triggered from any API 401)
		// On auth:cleared, we clear user state and redirect to login.
		// The event may fire multiple times if clearTokens() is called
		// in succession, but the redirect + state clear is idempotent.
		const handleAuthCleared = () => {
			console.warn('[AUTH:DEBUG] auth:cleared event received');
			console.trace('[AUTH:DEBUG] Stack trace for auth:cleared redirect');
			clearStoredUser();
			setUser(null);
			window.location.href = "/login";
		};
		window.addEventListener("auth:cleared", handleAuthCleared);
		return () => window.removeEventListener("auth:cleared", handleAuthCleared);
	}, []);

	// Login handler
	const login = useCallback(async (email: string, password: string) => {
		setIsLoading(true);

		try {
			const response = await api.post<LoginResponse>("/auth/login", {
				email,
				password,
			});

			if (response.error) {
				throw new Error(response.error.message);
			}

			if (response.data) {
				const { accessToken, refreshToken, user: userData } = response.data;

				// Store tokens
				tokenStorage.setAccessToken(accessToken);
				tokenStorage.setRefreshToken(refreshToken);

				// Store user
				storeUser(userData);
				setUser(userData);
			}
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Logout handler
	const logout = useCallback(() => {
		console.warn('[AUTH:DEBUG] logout() called');
		console.trace('[AUTH:DEBUG] Stack trace for logout');
		clearAuth();
		window.location.href = "/login";
	}, []);

	// Update user data
	const updateUser = useCallback((updatedUser: User) => {
		storeUser(updatedUser);
		setUser(updatedUser);
	}, []);

	return (
		<AuthContext.Provider
			value={{
				user,
				isAuthenticated: !!user,
				isLoading,
				login,
				logout,
				updateUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

// =====================
// Helper Functions
// =====================

function clearAuth() {
	console.warn('[AUTH:DEBUG] clearAuth() called');
	console.trace('[AUTH:DEBUG] Stack trace for clearAuth');
	tokenStorage.clearTokens();
	clearStoredUser();
	// Note: setUser(null) is called by the auth:cleared event handler
}

// =====================
// Hook
// =====================

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

// =====================
// Protected Route Component
// =====================

interface ProtectedRouteProps {
	children: ReactNode;
	allowedRoles?: User["role"][];
}

export function ProtectedRoute({
	children,
	allowedRoles,
}: ProtectedRouteProps) {
	const { user, isAuthenticated, isLoading } = useAuth();

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent" />
			</div>
		);
	}

	if (!isAuthenticated) {
		console.warn('[AUTH:DEBUG] ProtectedRoute: not authenticated, redirecting to /login');
		console.trace('[AUTH:DEBUG] Stack trace for ProtectedRoute redirect');
		window.location.href = "/login";
		return null;
	}

	if (allowedRoles && user && !allowedRoles.includes(user.role)) {
		console.warn('[AUTH:DEBUG] ProtectedRoute: wrong role, redirecting to /dashboard');
		console.trace('[AUTH:DEBUG] Stack trace for role redirect');
		window.location.href = "/dashboard";
		return null;
	}

	return <>{children}</>;
}
